import React, { useState, useEffect } from "react";
import {
    Paper,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Box,
    CircularProgress,
    Alert,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from "@mui/material";
import { Add as AddIcon, Delete } from "@mui/icons-material";
import dayjs from "dayjs";

import NotificationDialog from "./NotificationDialog";
import { getNotificationSchedule, deleteCompetitionNotificationSchedule } from "../../services/Competition";
import CustomSnackbar from '../../CustomSnackbar';

const formatNotify = (val) => {
    const map = {
        "all": "All",
        "all_evaluator": "All Evaluators",
        "all_participants": "All Participants",
        "top-01": "Top 1",
        "top-03": "Top 3",
        "top-10": "Top 10",
        "winning_evaluators": "Winning Evaluators",
    }
    return map[val] || val;
}

const NotificationScheduler = ({ competitionId }) => {
    const [openDialog, setOpenDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [deleteId, setDeleteId] = useState(null);
    const [schedules, setSchedules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [status, setStatus] = useState('');

    const loadSchedules = async () => {
        if (!competitionId) return;
        setLoading(true);
        setError("");
        try {
            const res = await getNotificationSchedule(competitionId);
            const list = res.data?.scheduled_notifications || [];
            setSchedules(list);
        } catch (e) {
            setError("Failed to load schedules");
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadSchedules();
    }, [competitionId]);


    const handleOpenCreate = () => {
        setOpenDialog(true);
    };

    const handleDelete = async (id) => {
        setOpenDeleteDialog(true);
        setDeleteId(id);
    };

    const handleDeleteConfirm = async () => {
        try {
            const res = await deleteCompetitionNotificationSchedule(competitionId, deleteId);
            console.log("Deleted notification schedule:", res);
            setOpenDeleteDialog(false);
            setStatus(res.message || 'Notification schedule deleted successfully!');
            setSchedules(schedules.filter(s => s.competition_notification_schedule_id !== deleteId));
        } catch (err) {
            console.error("Error deleting notification schedule:", err);
            setStatus(err.response?.data?.message || 'Failed to delete notification schedule.');
        }
    };


    return (
        <>
            <Paper elevation={0} sx={{ p: 2, mt: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Notification Scheduler</Typography>
                    <Button
                        disableElevation
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        sx={{ textTransform: "none" }}
                        onClick={handleOpenCreate}
                        disabled={loading}
                    >
                        Schedule New
                    </Button>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Manage scheduled notifications for participants.
                </Typography>

                {loading && (
                    <Box display="flex" justifyContent="center" my={3}>
                        <CircularProgress size={28} />
                    </Box>
                )}

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {!loading && !error && (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell><strong>Scheduled On</strong></TableCell>
                                    <TableCell><strong>Notify To</strong></TableCell>
                                    <TableCell><strong>Message (preview)</strong></TableCell>
                                    <TableCell align="center"><strong>Status</strong></TableCell>
                                    <TableCell align="right"><strong>Actions</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {schedules.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{ color: "text.secondary" }}>
                                            No notifications scheduled yet.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    schedules.map((s) => {
                                        const sent = s.is_sent === true;
                                        return (
                                            <TableRow key={s.competition_notification_schedule_id}>
                                                <TableCell>
                                                    {dayjs(s.scheduled_on, "YYYY-MM-DD HH:mm:ss").format(
                                                        "MMM D, YYYY [at] h:mm A"
                                                    )}
                                                </TableCell>
                                                <TableCell>{formatNotify(s.notify_to)}</TableCell>
                                                <TableCell
                                                    sx={{
                                                        maxWidth: 300,
                                                        whiteSpace: "nowrap",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                    }}
                                                    title={s.notification_message}
                                                >
                                                    {s.notification_message}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Chip
                                                        clickable={false}
                                                        onClick={(e) => e.stopPropagation()}
                                                        label={sent ? "Sent" : "Pending"}
                                                        color={sent ? "success" : "default"}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    {!sent && (
                                                        <IconButton onClick={() => handleDelete(s.competition_notification_schedule_id)}>
                                                            <Delete />
                                                        </IconButton>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>

            {/* ------------------- Dialog (create) ------------------- */}
            <NotificationDialog
                openDialog={openDialog}
                setOpenDialog={setOpenDialog}
                competitionId={competitionId}
                setSchedules={setSchedules}
            />
            {/* ------------------- Dialog (Delete confirmation) ------------------- */}
            <Dialog
                open={openDeleteDialog}
                onClose={() => setOpenDeleteDialog(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Delete Notification</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this notification schedule?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDeleteDialog(false)} color="inherit" sx={{ textTransform: "none" }}>Cancel</Button>
                    <Button
                        color="error"
                        variant="contained"
                        disableElevation
                        sx={{ textTransform: "none", ml: 1 }}
                        onClick={handleDeleteConfirm}
                    >
                        Delete</Button>
                </DialogActions>
            </Dialog>

            {status && (
                <CustomSnackbar
                    open={!!status}
                    message={status}
                    severity={status.includes('success') || status.includes('updated') ? 'success' : 'error'}
                    onClose={() => setStatus('')}
                />
            )}
        </>
    );
};

export default NotificationScheduler;